/**
 * Save snapshots (R10) — the serialization contract of architecture.md §12.
 *
 * @remarks
 * D23 made the fully-committed `RuntimeState` the whole snapshot: the
 * scheduler queue and the seeded RNG are IN it, so a restored save owes the
 * player the same future to the byte — pending timed events, PRNG draws, and
 * all. Everything else here is envelope: which bundle, which schema, an
 * integrity checksum over canonical JSON, and metadata game logic never reads.
 *
 * `savedAtIso` is deliberately unvalidated beyond being a string and is
 * NEVER read by game logic — it is display metadata, and the pure snapshot
 * builder takes it as an argument because the pure core reads no clock.
 *
 * `transcriptPolicy` travels WITH the save (§12): a snapshot truncated to its
 * last N entries says so, and a loader shows what it has rather than
 * pretending the missing history never happened.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { nonBlankStringSchema, nonNegativeIntegerSchema } from './primitives.js';
import { runtimeStateSchema } from './runtime-state/runtime-state.js';

/** A published bundle's content hash: 64 lowercase hex, the way the bundle store keys it. */
export const bundleHashSchema = z.string().regex(/^[0-9a-f]{64}$/u);

/** How much transcript a snapshot carries — the policy travels with the save. */
export const transcriptPolicySchema = z.union([
	z.strictObject({ type: z.literal('full') }).readonly(),
	z.strictObject({ keepLastEntries: nonNegativeIntegerSchema, type: z.literal('truncated') }).readonly(),
]);

/** How much transcript a snapshot carries — see {@link transcriptPolicySchema}. */
export type TranscriptPolicy = z.infer<typeof transcriptPolicySchema>;

/**
 * The envelope fields, shared by both schemas below (DRY — one field list).
 */
const saveSnapshotEnvelopeFieldSchemas = {
	/**
	 * The content hash of the PUBLISHED bundle this state was played on
	 * (D164) — the key it is fetched by, immutable — so a later open can tell
	 * that the game has moved on and replay the session across (D163).
	 * Absent for a pack that was never published under a hash (the shipped
	 * demo), which therefore never ports.
	 */
	bundleHash: bundleHashSchema.optional(),
	/** The pack this state belongs to (`manifest.gameId`) — a snapshot never loads into a different game. */
	bundleId: nonBlankStringSchema,
	/** The pack's declared schema version at save time (exact-match pre-1.0, like the loader). */
	bundleVersion: nonBlankStringSchema,
	/** FNV-1a 64-bit over the canonical JSON of `runtimeState` — sorted keys make it deterministic. */
	checksum: nonBlankStringSchema,
	/** A newer version the player chose to stay away from (D163's "stay"), so the next open does not ask again. */
	declinedBundleHash: bundleHashSchema.optional(),
	/** Which engine schema wrote this — the migration registry's key. */
	engineSchemaVersion: nonBlankStringSchema,
	/** Display metadata only; never read by game logic. */
	savedAtIso: nonBlankStringSchema,
	transcriptPolicy: transcriptPolicySchema,
} as const;

/**
 * The envelope ALONE, with `runtimeState` deliberately unvalidated (R11).
 *
 * @remarks
 * This is what `openSaveSnapshot` parses FIRST: an older save's state shape
 * is, by definition, not the current `runtimeStateSchema` — that is what the
 * migration registry exists to fix — so the state must stay opaque until the
 * migrations have walked it forward. Envelope first, migrate, THEN hold the
 * migrated state to the full current schema. Validating the state before
 * migrating would refuse every save the registry exists to rescue.
 */
export const saveSnapshotEnvelopeSchema = z
	.strictObject({
		...saveSnapshotEnvelopeFieldSchemas,
		/** Opaque until migrated — see the schema remarks. */
		runtimeState: z.unknown(),
	})
	.readonly();

/** The envelope with an unmigrated, unvalidated state — see {@link saveSnapshotEnvelopeSchema}. */
export type SaveSnapshotEnvelope = z.infer<typeof saveSnapshotEnvelopeSchema>;

/** One save snapshot — the §12 envelope around the whole future. */
export const saveSnapshotSchema = z
	.strictObject({
		...saveSnapshotEnvelopeFieldSchemas,
		/** The whole future: scheduler queue and randomness included (D23). */
		runtimeState: runtimeStateSchema,
	})
	.readonly();

/** One save snapshot — see {@link saveSnapshotSchema}. */
export type SaveSnapshot = z.infer<typeof saveSnapshotSchema>;
